import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, Col, Row, Skeleton } from 'antd'
import { BaseComponentProps } from 'employeeSignup/common/models'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { getBenefitDetails, setPlanPeriodRunoutDays } from 'core'
import finalizeQsehraBenefitDesign from 'core/application/adminDashboard/benefitDetails/design/qsehra/finalizeBenefit'
import { getProductClassDesignWizard } from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/getProductClassDesignWizard'
import { getProductClassDesignWizardState } from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/getProductClassDesignWizardState'
import getBenefitProductClasses, {
  BenefitProductClassResponse,
} from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import { EmployeeEligibilityType } from 'core/application/adminDashboard/benefitDetails/qsehra/benefitDesignState'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'
import { WizardStatus } from 'core/domain/wizard/wizard'

import { useCommandRequestV2, useQueryRequest } from 'common/hooks'

//
import global from '../../../../../common'
import { SimpleSelect } from '../../../../../common/components'
import { nowV2, toDateWithoutTimeZoneV2 } from '../../../../../common/dateFormatters'
import ProductClassWizardModal from '../../../common/productClassWizardModal'
import {
  QsehraProductClassWizard,
  QsehraProductClassWizardNavItemsMapping,
} from '../employeeClasses/productClassWizard/ProductClassStepsMapping'
import ProductClassDetails from '../employeeClasses/productClassWizard/productClassDetails'
import FinishDesignSuccessModal from './finishDesignSucessModal'

//
import './styles.less'

export type QsehraFinishBenefitDesignProps = {
  planPeriodId: string
  wizardStatus: WizardStatus
  employeeEligibilityType?: EmployeeEligibilityType
  benefitStartDate: string
}

type ProductClassModalStateProps = {
  productId?: string
  visible: boolean
  editingStep?: string
  name?: string
}

const QsehraFinishBenefitDesign: React.FC<BaseComponentProps<QsehraFinishBenefitDesignProps>> = (props) => {
  const { t: translate } = useTranslation()
  const { bindSubmitFormHandler } = props
  const [planPeriodDetails] = useQueryRequest(null, () => getBenefitDetails(props.state.planPeriodId))
  const [rounoutDays, setRunoutDays] = useState<number>(60)

  const [productClasses, areProductClassesLoading, , reloadProductClasses] = global.hooks.useQueryRequest<
    BenefitProductClassResponse[]
  >([], () => getBenefitProductClasses(props.state.planPeriodId))

  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })
  const [updateSummary, setUpdateSummary] = useState(false)
  const [colSpan, setColSpan] = useState(17)
  const [hasNotificationPassed, setHasNotificationPassed] = useState(true)
  const [postBenefitDesignCommand] = global.hooks.useCommandRequest()
  const [finalizeBenefitModalVisible, setFinalizeModalVisible] = useState<boolean>()

  useEffect(() => {
    if (!productClasses || productClasses.length == 0) {
      props.setIsFormValid(false)
      return
    }
    props.setIsFormValid(productClasses.filter((x) => !x.designCompleted).length == 0)
    setColSpan(productClasses[0].designCompleted ? 14 : 17)
  }, [productClasses])

  useEffect(() => {
    if (planPeriodDetails) {
      setRunoutDays(planPeriodDetails.runOutDays)
      setColSpan(planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Completed ? 14 : 17)
      if (planPeriodDetails.notificationDate) {
        setHasNotificationPassed(nowV2() > toDateWithoutTimeZoneV2(planPeriodDetails.notificationDate))
      } else {
        setHasNotificationPassed(false)
      }
    }
  }, [planPeriodDetails])

  const { sendCommand: sendPlanPeriodRunoutDays } = useCommandRequestV2((runOutDays: number) =>
    setPlanPeriodRunoutDays({
      planPeriodId: props.state.planPeriodId,
      runOutDays,
    }),
  )

  const onProductClassWizardModalClosed = async () => {
    await reloadProductClasses()

    setProductClassModalState({
      ...productClassModalState,
      visible: false,
    })
  }

  const onBenefitDesignEdit = (item: BenefitProductClassResponse) => {
    setProductClassModalState({
      productId: item.id,
      visible: true,
      editingStep: QsehraProductClassWizard.SetReimbursementProduct,
      name: item.name,
    })
  }

  const onSubmit = async () => {
    const result = await postBenefitDesignCommand(() =>
      finalizeQsehraBenefitDesign({
        planPeriodId: props.state.planPeriodId,
      }),
    )

    if (result.completed) {
      setFinalizeModalVisible(true)
      setTimeout(() => {
        setFinalizeModalVisible(false)
        props.finish()
      }, 4000)
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <>
      {productClassModalState.visible && (
        <ProductClassWizardModal
          navigationMapping={QsehraProductClassWizardNavItemsMapping}
          getProductClassWizard={(planPeriodId, productId) => getProductClassDesignWizard(productId)}
          getProductClassWizardState={(planPeriodId, productId) => getProductClassDesignWizardState(productId)}
          visible={productClassModalState.visible}
          id={productClassModalState.productId}
          planPeriodId={props.state.planPeriodId}
          afterClose={async () => await onProductClassWizardModalClosed()}
          editingStep={productClassModalState.editingStep}
          finishWizardMessage={`${translate(
            'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.finish-popup-class-design-wizard-message',
          ).replace(':className', productClassModalState.name)}`}
        />
      )}

      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={props.state}
        onSubmit={onSubmit}
        validate={(values) => {
          props.hasDirtyData(true)
          props.setIsFormValid(productClasses.length > 0)
          return {}
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          bindSubmitFormHandler(submitForm)
          return (
            <form onSubmit={handleSubmit} autoComplete="off" className={'qsehra-benefit-design-wrapper'}>
              <Row className={'mb-32'}>
                <Col span={17} className={'benefit_design_page_header'}>
                  <h5>{translate('admin-dashboard.benefit-details.qsehra-benefit-design.header')}</h5>
                  <h6 className={'inline mr-8'}>
                    {translate('admin-dashboard.benefit-details.qsehra-benefit-design.subheader')}
                  </h6>
                </Col>
              </Row>
              <Row>
                <Col span={17} className={'benefit_design_page_header'}>
                  <h5 className={'mt-0'}>
                    {translate('admin-dashboard.benefit-details.qsehra-benefit-design.your-class')}
                  </h5>
                </Col>
              </Row>
              <Skeleton loading={areProductClassesLoading}>
                {productClasses && (
                  <Row>
                    <Col span={24} className={'benefit_design_page_header'}>
                      {productClasses.map((item, index) => (
                        <ProductClassDetails
                          key={item.id}
                          collapseKey={index}
                          item={item}
                          onEdit={() => onBenefitDesignEdit(item)}
                          update={updateSummary}
                          // canEdit={planPeriodDetails?.benefitDesignStatus !== BenefitDesignStatus.Signed}
                          canEdit={planPeriodDetails?.isEditable}
                          setUpdate={setUpdateSummary}
                          employeeEligibilityType={props.state.employeeEligibilityType}
                          benefitStartDate={props.state.benefitStartDate}
                        />
                      ))}
                    </Col>
                  </Row>
                )}
              </Skeleton>
              {productClasses && (
                <>
                  <Row>
                    <Col span={17} className={'benefit_design_page_header'}>
                      <h5 className={'mt-24'}>Runout Period</h5>
                    </Col>
                  </Row>
                  <Row className={'mb-8'}>
                    <Col span={colSpan}>
                      <Card>
                        <Row>
                          <Col>
                            <div>
                              {hasNotificationPassed ? (
                                <div className="color-gray-8 mb-16">
                                  At the end of every calendar year, employees have a certain time period to submit any
                                  expenses incurred during the previous benefit year.
                                </div>
                              ) : (
                                <div className="color-gray-8 mb-16">
                                  At the end of every calendar year, employees have a certain time period to submit any
                                  expenses incurred during the previous benefit year. You can configure the length of
                                  this period, up to 90 days.
                                </div>
                              )}

                              <div className="mb-4">Annual Runout Period:</div>
                              {hasNotificationPassed ? (
                                <div>{rounoutDays} days</div>
                              ) : (
                                <SimpleSelect
                                  value={rounoutDays}
                                  className="w-auto"
                                  items={[
                                    { id: 0, name: '0 days' },
                                    { id: 30, name: '30 days' },
                                    { id: 60, name: '60 days' },
                                    { id: 90, name: '90 days' },
                                  ]}
                                  onChange={(value) => {
                                    sendPlanPeriodRunoutDays(value)
                                    setRunoutDays(value)
                                  }}
                                />
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </form>
          )
        }}
      </Formik>

      {finalizeBenefitModalVisible && (
        <FinishDesignSuccessModal
          subTitle={translate('admin-dashboard.benefit-details.success-modal.qsehra-end-benefit-design-subtitle')}
        />
      )}
    </>
  )
}

const formValidator = (translate: any) => Yup.object({})

export default QsehraFinishBenefitDesign
